import React, { Component } from 'react';

import { DataManagementShell } from 'web-ui-components/lib/layout/shells';
import {
  PageHeading,
  SectionHeading,
  SubHeading,
} from 'web-ui-components/lib/typography/headings';
import {
  ProminentValue,
  StandardValue,
} from 'web-ui-components/lib/typography/values';
import {
  SubBody,
  VisuallyHiddenBody,
} from 'web-ui-components/lib/typography/body';

import {
  BrandedPanel,
  ValidationMessageTile,
} from 'web-ui-components/lib/layout/content';
import { Inline } from 'web-ui-components/lib/layout/grids';

import {
  NavigationButton,
  NavigationButtonGroup,
} from 'web-ui-components/lib/navigation/buttons';

import { LoadingLocalTakeover } from 'web-ui-components/lib/organisms/takeovers';
import { Loader } from 'web-ui-components/lib/organisms/modals';

import {
  GenericRadio,
  StandaloneCheckbox,
} from 'web-ui-components/lib/data/capture';

import { GroupFieldWrapper } from 'web-ui-components/lib/layout/fieldWrappers';

import {
  requestCustomerDetails,
  updateContactPreference,
} from '../../utils/CustomerDetailsApiUtils';
import { isManagePersonalDetailsEnabled } from '../../utils/ManagePersonalDetailsUtils';

import InternetBankingActions from '../../actions/InternetBankingActions';
import { Success } from './Success';

const contactOptions = [
  { id: 'SMS', labelText: 'Text Message (SMS)' },
  { id: 'EMAIL', labelText: 'Email' },
  { id: 'PHONE', labelText: 'Phone' },
];

const ContactValue = ({ children, dataTestId }) => (
  <p data-test-id={dataTestId}>
    {children ?? 'You haven’t given us that yet.'}
  </p>
);

export class ContactPreferences extends Component {
  constructor(props) {
    super(props);

    this.state = {
      contactPreference: null,
      isBoxChecked: false,
      isError: false,
      isLoading: true,
      isUpdateInProgress: false,
      isUpdateCompleted: false,
    };
  }

  async componentDidMount() {
    try {
      const { contactPreference, ...data } = await requestCustomerDetails();
      this.setState({
        data,
        contactPreference,
        isLoading: false,
      });
    } catch (_error) {
      InternetBankingActions.navigateToWebTask('WEB-ERROR');
    }
  }

  handleSave = async () => {
    const { isBoxChecked } = this.state;

    if (!isBoxChecked) {
      this.setState({ isError: true });
      return;
    }

    try {
      this.setState({ isUpdateInProgress: true });
      await updateContactPreference(this.state.contactPreference);

      this.setState({ isUpdateInProgress: false, isUpdateCompleted: true });
    } catch (_error) {
      InternetBankingActions.navigateToWebTask('WEB-ERROR');
    }
  };

  render() {
    const {
      contactPreference,
      data,
      isBoxChecked,
      isError,
      isLoading,
      isUpdateCompleted,
      isUpdateInProgress,
    } = this.state;

    if (isUpdateCompleted) {
      return <Success />;
    }

    return (
      <LoadingLocalTakeover show={isLoading} title="Loading">
        <DataManagementShell>
          <PageHeading>Contact preferences</PageHeading>
          <BrandedPanel>
            <Inline.Justified
              nodes={[
                <SectionHeading>Contact details</SectionHeading>,
                isManagePersonalDetailsEnabled() && (
                  <NavigationButton.Secondary
                    data-test-id="change-contact-details"
                    labelText="Change contact details"
                    onClick={() =>
                      InternetBankingActions.navigateToWebTask(
                        'WEB-MANAGE-PERSONAL-DETAILS'
                      )
                    }
                  />
                ),
              ]}
            />
            <SubBody>
              <ProminentValue>Email</ProminentValue>
              <ContactValue dataTestId="contact-email">
                {data?.email}
              </ContactValue>
            </SubBody>
            <SubBody>
              <ProminentValue>Address</ProminentValue>
              <ContactValue dataTestId="contact-address">
                {data?.addressElements.length > 0
                  ? data.addressElements.map((item, index) => (
                      <span key={index}>
                        {item}
                        <br />
                      </span>
                    ))
                  : null}
              </ContactValue>
            </SubBody>
            <SubBody>
              <ProminentValue>Mobile</ProminentValue>
              <ContactValue dataTestId="contact-mobile">
                {data?.mobile}
              </ContactValue>
            </SubBody>
            <SubBody>
              <ProminentValue>Landline</ProminentValue>
              <ContactValue dataTestId="contact-landline">
                {data?.landline}
              </ContactValue>
            </SubBody>
          </BrandedPanel>
          <SubHeading>{this.props.content.contactPrefsHeader}</SubHeading>
          <StandardValue>
            <p>
              When it comes to security we’ll always try to contact you on your
              mobile.
            </p>
          </StandardValue>

          <GroupFieldWrapper
            titleContent={
              <VisuallyHiddenBody>
                {this.props.content.contactPrefsHeader}
              </VisuallyHiddenBody>
            }
          >
            <GenericRadio.StackedSet
              groupName="contact-options"
              items={contactOptions}
              onChange={(contactPreference) =>
                this.setState({ contactPreference })
              }
              selectedId={contactPreference}
            />
          </GroupFieldWrapper>
          <br />
          {isError && (
            <ValidationMessageTile messageText="Please tick this box to continue with your application" />
          )}
          <StandaloneCheckbox
            id="confirm-details"
            labelText="I confirm that my details are accurate and up-to-date"
            isChecked={isBoxChecked}
            onChange={() =>
              this.setState((prevState) => ({
                isBoxChecked: !prevState.isBoxChecked,
                isError: false,
              }))
            }
          />
          <NavigationButtonGroup
            primaryButton={
              <NavigationButton.Primary
                labelText="Save and continue"
                onClick={this.handleSave}
              />
            }
            secondaryButtons={
              <NavigationButton.Secondary
                labelText="Back"
                onClick={() =>
                  InternetBankingActions.navigateToWebTask('WEB-HOMEPAGE')
                }
              />
            }
          />
        </DataManagementShell>
        <Loader
          isOpen={isUpdateInProgress}
          titleText="This may take a few moments."
          body="Please don’t close your application."
        />
      </LoadingLocalTakeover>
    );
  }
}
